/**
 * HallyuSuperstore Integration Module
 * Exclusive package forwarding service for hallyusuperstore.com products
 */

class HallyuSuperstoreIntegration {
  constructor() {
    this.storeDomain = "hallyusuperstore.com";
    this.apiEndpoint = "https://hallyusuperstore.com/api";
    this.enabledProducts = new Map();
    this.productCache = new Map();
    this.init();
  }

  init() {
    this.loadEnabledProducts();
    this.initializeAdminControls();
  }

  /**
   * Load admin-enabled products from storage
   */
  loadEnabledProducts() {
    try {
      const saved = localStorage.getItem("hallyusuperstore_enabled_products");
      if (saved) {
        const products = JSON.parse(saved);
        products.forEach((product) => {
          this.enabledProducts.set(product.id, product);
        });
      }
    } catch (error) {
      console.error("Error loading enabled products:", error);
    }
  }

  /**
   * Validate if a product is from HallyuSuperstore and enabled for forwarding
   */
  validateProduct(productId, variantId = null) {
    // Check if product is in enabled list
    if (!this.enabledProducts.has(productId)) {
      return {
        valid: false,
        error: "This product is not available for package forwarding service",
      };
    }

    const product = this.enabledProducts.get(productId);

    // Check if product is still enabled
    if (!product.forwardingEnabled) {
      return {
        valid: false,
        error: "This product is temporarily unavailable for forwarding",
      };
    }

    // Check stock if variant specified
    if (variantId && product.variants) {
      const variant = product.variants.find((v) => v.id === variantId);
      if (!variant || variant.inventory_quantity <= 0) {
        return {
          valid: false,
          error: "This variant is out of stock",
        };
      }
    }

    return {
      valid: true,
      product: product,
    };
  }

  /**
   * Fetch product details from HallyuSuperstore
   */
  async fetchProductFromHallyuSuperstore(productHandle) {
    try {
      // Check cache first
      if (this.productCache.has(productHandle)) {
        return this.productCache.get(productHandle);
      }

      // Simulate API call to HallyuSuperstore
      const response = await axios.get(`/products/${productHandle}.js`);
      if (response.status !== 200) throw new Error("Product not found");

      const product = response.data;

      // Verify it's from HallyuSuperstore
      if (!this.isHallyuSuperstoreProduct(product)) {
        throw new Error("Product is not from HallyuSuperstore");
      }

      // Cache the product
      this.productCache.set(productHandle, product);

      return product;
    } catch (error) {
      console.error("Error fetching product:", error);
      return null;
    }
  }

  /**
   * Check if product belongs to HallyuSuperstore
   */
  isHallyuSuperstoreProduct(product) {
    // Check vendor
    const validVendors = ["HallyuSuperstore", "Hallyu Superstore", "HALLYU4U"];
    if (validVendors.includes(product.vendor)) return true;

    // Check tags
    if (product.tags && product.tags.includes("hallyusuperstore")) return true;

    // Check metafields
    if (product.metafields?.custom?.source === "hallyusuperstore") return true;

    return false;
  }

  /**
   * Admin function to enable/disable products
   */
  toggleProductForwarding(productId, enabled) {
    if (!this.isAdmin()) {
      console.error("Unauthorized: Admin access required");
      return false;
    }

    const product = this.enabledProducts.get(productId);
    if (product) {
      product.forwardingEnabled = enabled;
      this.saveEnabledProducts();
      return true;
    }
    return false;
  }

  /**
   * Admin function to add new product for forwarding
   */
  addProductForForwarding(product) {
    if (!this.isAdmin()) {
      console.error("Unauthorized: Admin access required");
      return false;
    }

    // Validate product structure
    if (!product.id || !product.title || !product.vendor) {
      console.error("Invalid product structure");
      return false;
    }

    // Add HallyuSuperstore specific metadata
    product.source = "hallyusuperstore";
    product.forwardingEnabled = true;
    product.addedAt = new Date().toISOString();
    product.lastSync = new Date().toISOString();

    this.enabledProducts.set(product.id, product);
    this.saveEnabledProducts();
    return true;
  }

  /**
   * Save enabled products to storage
   */
  saveEnabledProducts() {
    try {
      const products = Array.from(this.enabledProducts.values());
      localStorage.setItem(
        "hallyusuperstore_enabled_products",
        JSON.stringify(products)
      );

      // Trigger event for UI updates
      window.dispatchEvent(
        new CustomEvent("hallyusuperstore:products-updated", {
          detail: { products },
        })
      );
    } catch (error) {
      console.error("Error saving enabled products:", error);
    }
  }

  /**
   * Sync products with HallyuSuperstore
   */
  async syncWithHallyuSuperstore() {
    if (!this.isAdmin()) {
      console.error("Unauthorized: Admin access required");
      return { success: false, error: "Unauthorized" };
    }

    try {
      // Get all products from HallyuSuperstore
      const response = await axios.get("/products.json?limit=250");
      const data = response.data;

      let synced = 0;
      let errors = 0;

      for (const product of data.products) {
        if (this.isHallyuSuperstoreProduct(product)) {
          // Update or add product
          const existingProduct = this.enabledProducts.get(
            product.id.toString()
          );

          if (existingProduct) {
            // Update existing product
            Object.assign(existingProduct, {
              title: product.title,
              handle: product.handle,
              vendor: product.vendor,
              product_type: product.product_type,
              tags: product.tags,
              variants: product.variants,
              images: product.images,
              lastSync: new Date().toISOString(),
            });
          } else {
            // Add new product (disabled by default)
            this.addProductForForwarding({
              id: product.id.toString(),
              title: product.title,
              handle: product.handle,
              vendor: product.vendor,
              product_type: product.product_type,
              tags: product.tags,
              variants: product.variants,
              images: product.images,
              forwardingEnabled: false,
            });
          }
          synced++;
        }
      }

      this.saveEnabledProducts();

      return {
        success: true,
        synced,
        errors,
        total: data.products.length,
      };
    } catch (error) {
      console.error("Sync error:", error);
      return {
        success: false,
        error: error.message,
      };
    }
  }

  /**
   * Check if current user is admin
   */
  isAdmin() {
    const customer = window.HALLYU4U_CONFIG?.customer;
    return customer && customer.tags && customer.tags.includes("admin");
  }

  /**
   * Initialize admin controls if user is admin
   */
  initializeAdminControls() {
    if (!this.isAdmin()) return;

    // Add admin menu item
    const adminMenuItem = document.createElement("a");
    adminMenuItem.href = "/pages/admin-products";
    adminMenuItem.textContent = "🔧 Product Management";
    adminMenuItem.className = "admin-menu-item";

    const accountMenu = document.querySelector(".account-menu");
    if (accountMenu) {
      accountMenu.appendChild(adminMenuItem);
    }

    // Listen for sync requests
    window.addEventListener("hallyusuperstore:sync-request", async () => {
      const result = await this.syncWithHallyuSuperstore();
      window.dispatchEvent(
        new CustomEvent("hallyusuperstore:sync-complete", {
          detail: result,
        })
      );
    });
  }

  /**
   * Get product shipping restrictions
   */
  getProductRestrictions(productId) {
    const product = this.enabledProducts.get(productId);
    if (!product) return null;

    return {
      maxQuantity: product.maxQuantityPerOrder || 10,
      restrictedCountries: product.restrictedCountries || [],
      requiresSpecialHandling: product.requiresSpecialHandling || false,
      category: product.product_type,
      weight: product.weight || 0,
      dimensions: product.dimensions || null,
    };
  }

  /**
   * Validate cart for forwarding service
   */
  validateCart(cartItems) {
    const errors = [];
    const warnings = [];
    let totalWeight = 0;

    for (const item of cartItems) {
      // Check if product is enabled
      const validation = this.validateProduct(item.product_id, item.variant_id);

      if (!validation.valid) {
        errors.push({
          item: item.title,
          error: validation.error,
        });
        continue;
      }

      // Check restrictions
      const restrictions = this.getProductRestrictions(item.product_id);

      if (restrictions) {
        // Check quantity limits
        if (item.quantity > restrictions.maxQuantity) {
          warnings.push({
            item: item.title,
            warning: `Maximum quantity is ${restrictions.maxQuantity}`,
          });
        }

        // Calculate total weight
        totalWeight += restrictions.weight * item.quantity;
      }
    }

    // Check total weight limit (30kg for standard forwarding)
    if (totalWeight > 30000) {
      warnings.push({
        warning:
          "Total weight exceeds 30kg. Additional shipping charges may apply.",
      });
    }

    return {
      valid: errors.length === 0,
      errors,
      warnings,
      totalWeight,
    };
  }

  /**
   * Get enabled products for display
   */
  getEnabledProducts(category = null) {
    let products = Array.from(this.enabledProducts.values()).filter(
      (p) => p.forwardingEnabled
    );

    if (category) {
      products = products.filter((p) => p.product_type === category);
    }

    return products;
  }

  /**
   * Search enabled products
   */
  searchProducts(query) {
    const searchTerm = query.toLowerCase();
    return Array.from(this.enabledProducts.values())
      .filter((p) => p.forwardingEnabled)
      .filter(
        (p) =>
          p.title.toLowerCase().includes(searchTerm) ||
          p.vendor.toLowerCase().includes(searchTerm) ||
          (p.tags &&
            p.tags.some((tag) => tag.toLowerCase().includes(searchTerm)))
      );
  }
}

// Initialize integration
window.HallyuSuperstoreIntegration = new HallyuSuperstoreIntegration();

// Export for use in other modules
if (typeof module !== "undefined" && module.exports) {
  module.exports = HallyuSuperstoreIntegration;
}
